<template>
  <loan-status-alert-container
    :loan="loan"
    :borrower-action-required="
      needsMoreInformation || needsBorrowerValidation || needsAutoValidation
    "
    :owner-action-required="needsMoreInformation || needsOwnerValidation"
  >
    <template #borrower>
      <i18n
        v-if="loan.loanable.return_instructions"
        path="components.loan.status.alerts.ended.borrower.return_instructions_message"
        tag="p"
      >
        <template #return_instructions
          ><icon-button
            variant="ghost-secondary"
            size="inline"
            @click="$emit('jump', sections.returnInstructions)"
            >{{
              $t("components.loan.status.alerts.ended.borrower.return_instructions")
            }}</icon-button
          ></template
        >
      </i18n>

      <p v-if="needsMoreInformation">
        <i18n
          path="components.loan.status.alerts.ended.missing_info_message"
          tag="span"
          class="text-nowrap"
        >
          <template #missing_info>
            <icon-button
              variant="ghost-secondary"
              size="inline"
              @click="$emit('jump', sections.info)"
              >{{ $t("components.loan.status.alerts.ended.missing_info") }}</icon-button
            ></template
          >
        </i18n>
      </p>

      <i18n
        v-else-if="needsAutoValidation"
        path="components.loan.status.alerts.ended.borrower.auto_validation_message"
        tag="p"
      >
        <template #auto_validation>
          <icon-button
            variant="ghost-secondary"
            size="inline"
            @click="$emit('jump', sections.autoValidation)"
            >{{ $t("components.loan.status.alerts.ended.auto_validation") }}</icon-button
          >
        </template>
      </i18n>

      <i18n
        v-else-if="needsBorrowerValidation"
        path="components.loan.status.alerts.ended.owner.validation.message"
        tag="p"
      >
        <template #date>{{ validationLimit | datetime }}</template>
        <template #info>
          <icon-button
            variant="ghost-secondary"
            size="inline"
            @click="$emit('jump', { ...sections.info, open: false })"
            >{{ $t("components.loan.status.alerts.ended.owner.validation.info") }}</icon-button
          >
        </template>
      </i18n>

      <p v-else-if="needsValidation">
        {{ $t("components.loan.status.alerts.ended.borrower.owner_validation.message") }}

        <template v-if="borrowerMustAddToBalance"
          ><i18n
            path="components.loan.status.alerts.ended.borrower.owner_validation.add_balance_message"
          >
            <template #add_balance
              ><icon-button
                variant="ghost-secondary"
                size="inline"
                @click="$emit('jump', sections.contribution)"
                >{{
                  $t("components.loan.status.alerts.ended.borrower.owner_validation.add_balance")
                }}</icon-button
              ></template
            >
          </i18n>
        </template>
      </p>

      <template v-else>
        <p>{{ $t("components.loan.status.alerts.ended.regular_state.message") }}</p>
        <icon-button variant="ghost-secondary" size="sm" :onclick="validateToFixInvalidState">{{
          $t("components.loan.status.alerts.ended.regular_state.continue")
        }}</icon-button>
      </template>

      <i18n
        v-if="needsMoreInformation"
        path="components.loan.status.alerts.ended.borrower.extension_message"
        tag="p"
      >
        <template #extension>
          <icon-button
            variant="ghost-secondary"
            size="inline"
            @click="$emit('jump', sections.dates)"
            >{{ $t("components.loan.status.alerts.ended.borrower.extension") }}</icon-button
          >
        </template>
      </i18n>
    </template>

    <template #owner>
      <p v-if="needsMoreInformation">
        <i18n
          path="components.loan.status.alerts.ended.missing_info_message"
          tag="span"
          class="text-nowrap"
        >
          <template #missing_info>
            <icon-button
              variant="ghost-secondary"
              size="inline"
              @click="$emit('jump', sections.info)"
              >{{ $t("components.loan.status.alerts.ended.missing_info") }}</icon-button
            ></template
          >
        </i18n>
      </p>

      <i18n
        v-else-if="ownerMustAddToBalance"
        path="components.loan.status.alerts.ended.owner.must_add_to_balance"
        tag="p"
      />

      <i18n
        v-else-if="needsOwnerValidation"
        path="components.loan.status.alerts.ended.validation_message"
        tag="p"
      >
        <template #validation_date>{{ validationLimit | datetime }}</template>
        <template #validation_informations>
          <icon-button
            variant="ghost-secondary"
            size="inline"
            @click="$emit('jump', { ...sections.info, open: false })"
            >{{ $t("components.loan.status.alerts.ended.validation_informations") }}</icon-button
          >
        </template>
      </i18n>

      <i18n
        v-else-if="needsAutoValidation"
        path="components.loan.status.alerts.ended.must_auto_validate_message"
        tag="p"
      >
        <template #name>
          {{ loan.borrower_user.name }}
        </template>
        <template #auto_validation>
          <icon-button
            variant="ghost-secondary"
            size="inline"
            @click="$emit('jump', sections.autoValidation)"
            >{{ $t("components.loan.status.alerts.ended.auto_validation") }}</icon-button
          >
        </template>
      </i18n>
      <p v-else-if="needsValidation">
        {{
          $t("components.loan.status.alerts.ended.borrower.owner_validation.must_validate", {
            name: loan.borrower_user.name,
          })
        }}
      </p>
      <template v-else>
        <p>{{ $t("components.loan.status.alerts.ended.regular_state.message") }}</p>
        <icon-button variant="ghost-secondary" size="sm" :onclick="validateToFixInvalidState">{{
          $t("components.loan.status.alerts.ended.regular_state.continue")
        }}</icon-button>
      </template>
    </template>
    <template #admin>
      <p v-if="needsMoreInformation">
        {{ $t("components.loan.status.alerts.ended.admin.missing_info") }}
      </p>
      <p v-else-if="needsBorrowerValidation || needsOwnerValidation">
        {{ $t("components.loan.status.alerts.ended.admin.validation.part_1") }}
        <template v-if="needsBorrowerValidation">{{ loan.borrower_user.full_name }}</template>
        <template v-if="needsBorrowerValidation && needsOwnerValidation">
          {{ $t("components.loan.status.alerts.ended.admin.validation.and") }}</template
        >
        <template v-if="needsOwnerValidation">
          {{ $t("components.loan.status.alerts.ended.admin.validation.owner") }}</template
        >
        {{ $t("components.loan.status.alerts.ended.admin.validation.part_2") }}
      </p>
      <p v-else-if="needsAutoValidation">
        {{ loan.borrower_user.name }} doit compléter
        <icon-button
          variant="ghost-secondary"
          size="inline"
          @click="$emit('jump', sections.autoValidation)"
          >l'auto-validation</icon-button
        >.
      </p>
      <template v-else>
        <p>{{ $t("components.loan.status.alerts.ended.regular_state.message") }}</p>
        <icon-button variant="ghost-secondary" size="sm" :onclick="validateToFixInvalidState">{{
          $t("components.loan.status.alerts.ended.regular_state.correct")
        }}</icon-button>
      </template>
    </template>
  </loan-status-alert-container>
</template>

<script>
import LoanStatusAlertContainer from "@/components/Loan/Status/Alerts/LoanStatusAlertContainer.vue";
import IconButton from "@/components/shared/IconButton.vue";
import { isCoownerOrOwner } from "@/helpers/permissions/loanables";
import { isBorrower } from "@/helpers/permissions/loans";
import { put } from "@/requests/server";
import dayjs from "dayjs";
import { sections } from "@/components/Loan/FullLoanSections";
export default {
  name: "LoanEnded",
  components: {
    IconButton,
    LoanStatusAlertContainer,
  },
  props: {
    loan: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      sections,
    };
  },
  computed: {
    isBorrower() {
      return isBorrower(this.$store.state.user, this.loan);
    },
    isCoownerOrOwner() {
      return isCoownerOrOwner(this.$store.state.user, this.loan.loanable);
    },
    needsMoreInformation() {
      return (
        this.loan.requires_detailed_mileage &&
        (this.loan.mileage_start === null || this.loan.mileage_end === null)
      );
    },
    needsValidation() {
      return this.loan.needs_validation;
    },

    needsOwnerValidation() {
      return this.needsValidation && !this.loan.owner_validated_at;
    },
    needsBorrowerValidation() {
      return this.needsValidation && !this.loan.borrower_validated_at;
    },
    needsAutoValidation() {
      return (
        this.loan.loanable.library?.loan_auto_validation_question && !this.loan.auto_validated_at
      );
    },
    borrowerMustAddToBalance() {
      return (
        Number.parseFloat(this.$store.state.user.balance) <
        -this.loan.borrower_invoice.user_balance_change
      );
    },
    ownerMustAddToBalance() {
      return (
        Number.parseFloat(this.$store.state.user.balance) +
          this.loan.owner_invoice.user_balance_change <
        0
      );
    },
    validationLimit() {
      if (this.loan.attempt_autocomplete_at) {
        return this.loan.attempt_autocomplete_at;
      }

      return dayjs(this.loan.actual_return_at).add(2, "days");
    },
  },
  methods: {
    async validateToFixInvalidState() {
      const { data } = await put(
        `/loans/${this.loan.id}/validate`,
        {},
        {
          notifications: {
            action: this.$t("components.loan.status.alerts.ended.notifications.correct.action"),
          },
        }
      );
      this.$emit("input", data);
    },
  },
};
</script>

<style lang="scss"></style>
